import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

// PrimeNG Modules
import { TabsModule } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { AvatarModule } from 'primeng/avatar';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { ConfirmationService, MessageService } from 'primeng/api';

// Models & Services
import { Customer } from '../../customer/customer.model';
import { CustomerService } from '../../customer/customer.service';
import { TenantScopeService } from '../tenant-scope.service';
import { TenantRoleService } from './tenant-role.service';
import { CustomRoleSummary, CustomRoleDetail } from './tenant-role.model';
import { CustomerFormComponent } from '../../customer/customer-form/customer-form.component';
import { CustomerRolesComponent } from '../../customer/customer-roles/customer-roles.component';
import { CustomRoleBuilderComponent } from './custom-role-builder/custom-role-builder.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { SessionStorageService } from '../../services/session-storage.service';

@Component({
    selector: 'app-users-and-roles',
    templateUrl: './users-and-roles.component.html',
    styleUrls: ['./users-and-roles.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        TabsModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        TagModule,
        AvatarModule,
        ToastModule,
        TooltipModule,
        ConfirmDialogModule,
        DialogModule,
        CustomerFormComponent,
        CustomerRolesComponent,
        CustomRoleBuilderComponent,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [MessageService, ConfirmationService]
})
export class UsersAndRolesComponent implements OnInit {
    activeTabIndex: number = 0;
    companyName: string = '';

    get metrics(): PageMetric[] {
        return [
            { label: 'Employees', value: this.customers.length, tone: 'info' },
            { label: 'Available Roles', value: this.roles.length, tone: 'info' },
            { label: 'Custom Roles', value: this.customRolesCount, tone: 'success' },
            { label: 'System Templates', value: this.standardRolesCount, tone: 'default' }
        ];
    }

    // --- Tab 1: Employees State ---
    customers: Customer[] = [];
    employeeSearch: string = '';
    isLoadingEmployees: boolean = false;
    showCustomerForm: boolean = false;
    customerFormHeader: string = 'Create New Employee';
    customerFormSubmitLabel: string = 'Create';
    selectedCustomer: Customer | null = null;
    showCustomerRolesDialog: boolean = false;

    // --- Tab 2: Roles State ---
    roles: CustomRoleSummary[] = [];
    roleSearch: string = '';
    roleFilter: 'ALL' | 'CUSTOM' | 'STANDARD' = 'ALL';
    isLoadingRoles: boolean = false;

    // Custom Role Builder Modal State
    showRoleBuilder: boolean = false;
    editingRoleId: number | null = null;

    // Read-only Standard Role Inspector
    showStandardRoleInspector: boolean = false;
    inspectedRole: CustomRoleDetail | null = null;
    isLoadingInspectedRole: boolean = false;

    companyId: number | null = null;

    constructor(
        private customerService: CustomerService,
        private tenantRoleService: TenantRoleService,
        private tenantScopeService: TenantScopeService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.companyName = this.sessionStorageService.getCompany()?.name || '';
        const activeScope = this.tenantScopeService.getActiveScope();
        if (activeScope?.type === 'COMPANY') {
            this.companyId = activeScope.companyId;
            this.loadEmployees();
            this.loadRolesDirectory();
        } else {
            this.tenantScopeService.activeScope$.subscribe(scope => {
                if (scope?.type === 'COMPANY') {
                    this.companyId = scope.companyId;
                    this.loadEmployees();
                    this.loadRolesDirectory();
                }
            });
        }
    }

    refreshAll(): void {
        this.loadEmployees();
        this.loadRolesDirectory();
    }

    onTabChange(tab: string | number): void {
        this.activeTabIndex = Number(tab) || 0;
    }

    // --- Employees Management ---

    loadEmployees(): void {
        if (!this.companyId) return;
        this.isLoadingEmployees = true;
        this.customerService.getCustomersByCompany(this.companyId).subscribe({
            next: (customers: Customer[]) => {
                this.customers = customers ?? [];
                this.isLoadingEmployees = false;
            },
            error: (err: any) => {
                console.error('Failed to load employees', err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load employees.' });
                this.isLoadingEmployees = false;
            }
        });
    }

    get visibleCustomers(): Customer[] {
        const text = this.employeeSearch.trim().toLowerCase();
        if (!text) return this.customers;
        return this.customers.filter(c =>
            `${c.name || ''} ${c.emailAddress || ''} ${c.phoneNumber || ''}`
                .toLowerCase()
                .includes(text)
        );
    }

    createNewEmployee(): void {
        this.customerFormHeader = 'Create New Employee';
        this.customerFormSubmitLabel = 'Create';
        this.selectedCustomer = new Customer();
        this.showCustomerForm = true;
    }

    editEmployee(customer: Customer): void {
        this.customerFormHeader = 'Edit Employee';
        this.customerFormSubmitLabel = 'Update';
        this.selectedCustomer = customer;
        this.showCustomerForm = true;
    }

    onCustomerFormClosed(): void {
        this.showCustomerForm = false;
        this.loadEmployees();
    }

    manageRolesForEmployee(customer: Customer): void {
        this.selectedCustomer = customer;
        this.showCustomerRolesDialog = true;
    }

    onCustomerRolesDialogClosed(): void {
        this.showCustomerRolesDialog = false;
        this.loadEmployees();
        this.loadRolesDirectory();
    }

    sendEmail(email?: string): void {
        if (email && !email.includes('@abcxyz.com')) {
            window.location.href = `mailto:${email}`;
        }
    }

    makePhoneCall(phone?: string): void {
        if (phone && phone !== 'N/A') {
            window.location.href = `tel:${phone}`;
        }
    }

    // --- Roles Management ---

    loadRolesDirectory(): void {
        this.isLoadingRoles = true;
        this.tenantRoleService.getRolesDirectory().subscribe({
            next: roles => {
                this.roles = roles ?? [];
                this.isLoadingRoles = false;
            },
            error: err => {
                console.error('Failed to load roles directory', err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load roles.' });
                this.isLoadingRoles = false;
            }
        });
    }

    get filteredRoles(): CustomRoleSummary[] {
        let list = this.roles;
        if (this.roleFilter === 'CUSTOM') {
            list = list.filter(r => r.custom);
        } else if (this.roleFilter === 'STANDARD') {
            list = list.filter(r => !r.custom);
        }

        const query = this.roleSearch.trim().toLowerCase();
        if (query) {
            list = list.filter(r =>
                r.roleName.toLowerCase().includes(query) ||
                (r.description || '').toLowerCase().includes(query) ||
                r.stitchedRoleNames.some(s => s.toLowerCase().includes(query))
            );
        }
        return list;
    }

    get customRolesCount(): number {
        return this.roles.filter(r => r.custom).length;
    }

    get standardRolesCount(): number {
        return this.roles.filter(r => !r.custom).length;
    }

    openCreateCustomRole(): void {
        this.editingRoleId = null;
        this.showRoleBuilder = true;
    }

    editCustomRole(role: CustomRoleSummary): void {
        this.editingRoleId = role.roleId;
        this.showRoleBuilder = true;
    }

    inspectStandardRole(role: CustomRoleSummary): void {
        this.isLoadingInspectedRole = true;
        this.showStandardRoleInspector = true;
        this.tenantRoleService.getRoleDetail(role.roleId).subscribe({
            next: detail => {
                this.inspectedRole = detail;
                this.isLoadingInspectedRole = false;
            },
            error: err => {
                console.error('Failed to load role detail', err);
                this.isLoadingInspectedRole = false;
            }
        });
    }

    deleteCustomRole(role: CustomRoleSummary): void {
        if (role.assignedUsersCount > 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Role In Use',
                detail: `Cannot delete '${role.roleName}' because it is assigned to ${role.assignedUsersCount} active employee(s). Please reassign them first.`
            });
            return;
        }

        this.confirmationService.confirm({
            message: `Are you sure you want to delete custom role '${role.roleName}'?`,
            header: 'Delete Custom Role',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.tenantRoleService.deleteCustomRole(role.roleId).subscribe({
                    next: () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Deleted',
                            detail: `Custom role '${role.roleName}' deleted.`
                        });
                        this.loadRolesDirectory();
                    },
                    error: err => {
                        const msg = err?.error?.message || err?.message || 'Failed to delete role.';
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
                    }
                });
            }
        });
    }

    onRoleBuilderClosed(saved: boolean): void {
        this.showRoleBuilder = false;
        this.editingRoleId = null;
        if (saved) {
            this.loadRolesDirectory();
        }
    }
}
